import { CaseDefaultsService } from '../../app-create-case/services/case-defaults.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { InsuranceCase } from '../../app-create-case/models/insurance-case.model';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { EcapQueryHandlerResponse, Filter, GqlWatchResponse } from '../../shared/models/common.model';
import { CommonService } from '../../shared/services/common.service';
import { GqlQueryBuilderService } from '../../shared/services/gql-query-builder.service';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import { GraphqlService } from '../../shared/services/graphql.service';
import { ReportService } from '../../shared/services/reports.service';
import { CaseSearchFilter } from '../models/case-search-filter';
import {
    GetCollectiveInvoicesQueryRequestModel,
    GetInsuranceCaseQueryRequestModel
} from '../../shared/models/request.model';
import { mergeAllObjects } from '../../shared/static-methods';
import { CollectiveInvoiceModel } from '../../app-master-data/components/Model/CollectiveInvoiceModel';
import { BexioService } from '../../shared/services/bexio.service';
import { SendBexioCollectiveInvoiceCommand } from '../../shared/models/commandModels/SendBexioCollectiveInvoiceCommand';
import { IpexUserInformationService } from '../../shared/services/ipex-user-information.service';
import { EcapGql } from '../../shared/standalone-packages/ecap3/app-platform-graphql';
import { ShellDomainProvider, UserInfoService } from '../../shared/standalone-packages/ecap3/core';
import { PCXKeyValue } from '../../shared/models/domain/PCXKeyValue';
import SendCaseMailAndInvoiceCommand from '../../shared/models/commandModels/SendCaseMailAndInvoiceCommand';
import { IpexCommandResponse } from '../../shared/models/responseModels/IpexCommandResponse';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class CasesService {
    private readonly urlResolver = inject(UrlResolverService);
    public static hasAdditionalSearchFilterData = false;
    public static additionalSearchFilterData$ = new BehaviorSubject<CaseSearchFilter>(null);
    public static userSearchFilterPreviousState$ = new BehaviorSubject<CaseSearchFilter>(new CaseSearchFilter());
    public static jarowaSearchFilterData$ = new BehaviorSubject<CaseSearchFilter>(new CaseSearchFilter());
    finalInspectorDetails$ = new BehaviorSubject<any>(null);
    dataTableModelOfSelectedCase = new BehaviorSubject<any>(null);
    header: HttpHeaders = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    emailReg = SCConfig.EMAIL_REGEX;

    constructor(
        private readonly gqlQueryCommandService: GqlQueryCommandService,
        private readonly graphqlService: GraphqlService,
        private readonly formBuilder: FormBuilder,
        private readonly commonService: CommonService,
        private readonly reportService: ReportService,
        private readonly gqlQueryBuilderService: GqlQueryBuilderService,
        private readonly GQL: EcapGql,
        private readonly http: HttpClient,
        private readonly caseDefaultService: CaseDefaultsService,
        private readonly bexioService: BexioService,
        private readonly ipexUserInformationService: IpexUserInformationService,
        private readonly userInfoService: UserInfoService
    ) {}

    getCasesList(
        caseFilters: CaseSearchFilter,
        sortBy = 'CreateDate: -1',
        pageNumber = 0,
        pageSize = 10
    ): Observable<GqlWatchResponse<InsuranceCase>> {
        const entityName = SCConfig.Entity.InsuranceCase;
        const fields = SCConfig.UserReadableDatas.InsuranceCase;
        const filters = this.getFiltersFromCaseSearchCriteria(caseFilters);
        return this.gqlQueryCommandService.gqlWatchQuery<InsuranceCase>(
            entityName,
            fields,
            filters,
            sortBy,
            pageNumber,
            pageSize
        );
    }

    public getFiltersFromCaseSearchCriteria(
        caseFilters: CaseSearchFilter = null,
        jarowaType: boolean = false
    ): Filter[] {
        const filters: Filter[] = jarowaType
            ? []
            : [
                  new Filter('IsDraft', '=', false),
                  new Filter('IsMarkedToDelete', '=', false),
                  new Filter('CaseCreationStatus', '=', SCConfig.Case_Creation_Status.IPEXCREATE)
              ];
        if (jarowaType) {
            filters.push(new Filter('IsFromJarowa', '=', true));
            if (caseFilters != null && caseFilters.CaseStatus && caseFilters.CaseStatus.Key === 'AUDIT_PHASE') {
                filters.push(new Filter('IsDraft', '=', false));
                filters.push(new Filter('CaseCreationStatus', '=', SCConfig.Case_Creation_Status.IPEXCREATE));
            }
        }

        if (caseFilters === null) {
            return filters;
        }

        const {
            CaseStatus,
            MultipleCaseStatus,
            ClaimNumber,
            CaseReportNumber,
            Department,
            InsuranceCompany,
            OrderFrom,
            OrderTo,
            InspectorUserId,
            BackofficeUserId,
            QualityCheck,
            EmailSendFrom,
            EmailSendTo,
            CreateDateFrom,
            CreateDateTo,
            IsMarkedForQualityCheck,
            InspectorStatus,
            SLAType,
            DocumentProcessingStatus,
            SLAStartDate,
            SLAExpiredDate,
            IsRegulated,
            BexioInvoiceId,
            IsDraft,
            Language,
            ServiceProductId,
            ReadyForBillingFromDate,
            ReadyForBillingToDate,
            InvoiceFromDate,
            InvoiceToDate,
            IsQuickSearch,
            SearchKey,
            AllCases,
            LoggedUserInfo,
            ConnectedRepairCaseFilter
        } = caseFilters;
        if (CaseStatus || (MultipleCaseStatus?.length > 0 && !IsQuickSearch)) {
            const searchKeys =
                MultipleCaseStatus?.length > 0 ? MultipleCaseStatus.map(item => item.Key) : [CaseStatus.Key];
            filters.push(new Filter(`'CaseStatus.Key'`, 'in', searchKeys));
        } else if (!IsQuickSearch) {
            if (!ClaimNumber && !CaseReportNumber && !jarowaType) {
                const userInfo = this.userInfoService.getUserInfo();
                const isOnlyInspector =
                    userInfo.Roles.includes(SCConfig.Roles.Inspector) &&
                    !userInfo.Roles.includes(SCConfig.Roles.Clearing);
                if (isOnlyInspector) {
                    filters.push(
                        new Filter(
                            null,
                            'raw',
                            `'CaseStatus.Key': {$nin: ["${SCConfig.CaseStatus.CANCELLATION.Key}", "${SCConfig.CaseStatus.CLEARING.Key}"]}`
                        )
                    );
                } else {
                    filters.push(new Filter(`'CaseStatus.Key'`, '!=', `'${SCConfig.CaseStatus.CANCELLATION.Key}'`));
                }
            }
        }
        if (ClaimNumber) {
            filters.push(new Filter('InsuranceClaimNumber', 'search', CommonService.sanitizeSearchString(ClaimNumber)));
        }

        if (CaseReportNumber) {
            filters.push(
                new Filter('CaseReportNumber', 'search', CommonService.sanitizeSearchString(CaseReportNumber))
            );
        }

        if (InsuranceCompany) {
            filters.push(new Filter(`'CaseReferenceInfo.InsuranceComapnayId'`, '=', InsuranceCompany.ItemId));
        }

        if (Department) {
            filters.push(new Filter(`'CaseReferenceInfo.DepartmentId'`, '=', Department.DepartmentId));
        }

        if (InspectorUserId) {
            // while quick search is not active
            if (!IsQuickSearch && !AllCases) {
                filters.push(new Filter(`'InspectorUserId'`, '=', InspectorUserId));
            }
        }

        if (BackofficeUserId) {
            filters.push(new Filter(`'BackofficeUserId'`, '=', BackofficeUserId));
        }

        if (InvoiceFromDate || InvoiceToDate) {
            const searchValue = this.commonService.getSearchValueByFromAndToDate(
                InvoiceFromDate,
                InvoiceToDate,
                'CaseInvoice.GenerationDate'
            );
            filters.push(new Filter(null, 'raw', searchValue));
        }

        if (OrderFrom || OrderTo) {
            const searchValue = this.commonService.getSearchValueByFromAndToDate(OrderFrom, OrderTo, 'OrderDate');
            filters.push(new Filter(null, 'raw', searchValue));
        }

        if (ReadyForBillingFromDate || ReadyForBillingToDate) {
            const searchValue = this.commonService.getSearchValueByFromAndToDate(
                ReadyForBillingFromDate,
                ReadyForBillingToDate,
                'ReadyForBillingDate'
            );
            filters.push(new Filter(null, 'raw', searchValue));
        }

        if (EmailSendFrom || EmailSendTo) {
            let today_start;
            let today_end;
            if (EmailSendFrom && EmailSendTo) {
                today_start = this.commonService.getDateToUTC(new Date(EmailSendFrom), true);
                today_end = this.commonService.getDateToUTC(new Date(EmailSendTo), false);
            } else if (EmailSendFrom) {
                today_start = this.commonService.getDateToUTC(new Date(EmailSendFrom), true);
                today_end = this.commonService.getDateToUTC(new Date(2100, 7, 20, 10, 33, 30, 0), false);
            } else {
                today_start = this.commonService.getDateToUTC(new Date(1996, 7, 20, 10, 33, 30, 0), true);
                today_end = this.commonService.getDateToUTC(new Date(EmailSendTo), false);
            }

            const orQueries = [];
            orQueries.push(
                `'$or' : [
                    {'ClosedDate': {'$gte' : ISODate('${today_start}'), '$lte' : ISODate('${today_end}')}}
                ]`
            );
            filters.push(new Filter(null, 'raw', orQueries[0]));
        }

        if (IsMarkedForQualityCheck || IsMarkedForQualityCheck == false || QualityCheck || InspectorStatus) {
            filters.push(new Filter('QualityCheckInfo', '!=', 'null'));
            filters.push(new Filter(`'QualityCheckInfo.IsMarkedForQualityCheck'`, '=', IsMarkedForQualityCheck));
        }

        if (QualityCheck) {
            filters.push(new Filter(`'QualityCheckInfo.AdminQualityCheckInfo'`, '!=', 'null'));
            filters.push(new Filter(`'QualityCheckInfo.AdminQualityCheckInfo.Status'`, '!=', 'null'));
            filters.push(new Filter(`'QualityCheckInfo.AdminQualityCheckInfo.Status.Key'`, '=', QualityCheck.Key));
        }

        if (InspectorStatus) {
            filters.push(new Filter(`'QualityCheckInfo.InspectorQualityCheckInfo'`, '!=', 'null'));
            filters.push(new Filter(`'QualityCheckInfo.InspectorQualityCheckInfo.Status'`, '!=', 'null'));
            filters.push(
                new Filter(`'QualityCheckInfo.InspectorQualityCheckInfo.Status.Key'`, '=', InspectorStatus.Key)
            );
        }

        if (Language) {
            filters.push(new Filter(`'Language'`, '=', Language));
        }

        if (ServiceProductId) {
            filters.push(new Filter(`'UploadIpexAdditionalInfo.ServiceProductItemId'`, '=', ServiceProductId));
        }

        if (CreateDateFrom || CreateDateTo) {
            let today_start;
            let today_end;
            if (CreateDateFrom && CreateDateTo) {
                today_start = this.commonService.getDateToUTC(new Date(CreateDateFrom), true);
                today_end = this.commonService.getDateToUTC(new Date(CreateDateTo), false);
            } else if (CreateDateFrom) {
                today_start = this.commonService.getDateToUTC(new Date(CreateDateFrom), true);
                today_end = this.commonService.getDateToUTC(new Date(), false);
            } else {
                today_start = this.commonService.getDateToUTC(new Date(1996, 7, 20, 10, 33, 30, 0), true);
                today_end = this.commonService.getDateToUTC(new Date(CreateDateTo), false);
            }

            if (jarowaType) {
                filters.push(
                    new Filter(
                        null,
                        'raw',
                        `OrderDate: {'$gte' : ISODate('${today_start}'), '$lte' : ISODate('${today_end}')}`
                    )
                );
            } else {
                filters.push(
                    new Filter(
                        null,
                        'raw',
                        `CreateDate: {'$gte' : ISODate('${today_start}'), '$lte' : ISODate('${today_end}')}`
                    )
                );
            }
        }

        if (SLAType) {
            filters.push(new Filter(`'SLAType'`, '=', SLAType));
        }

        if (DocumentProcessingStatus) {
            if (DocumentProcessingStatus == SCConfig.DocumentProcessingStatus.SUCCESS) {
                const orQueries = [];
                orQueries.push(
                    `'$or' : [{'DocumentProcessingStatus': ''}, {'DocumentProcessingStatus': null}, {'DocumentProcessingStatus': '${DocumentProcessingStatus}'}]`
                );

                filters.push(new Filter(null, 'raw', orQueries[0]));
            } else {
                filters.push(new Filter(`'DocumentProcessingStatus'`, '=', DocumentProcessingStatus));
            }
        }

        if (SLAStartDate) {
            const today_start = this.commonService.getDateToUTC(new Date(SLAStartDate), true);
            const today_end = this.commonService.getDateToUTC(new Date(SLAStartDate), false);
            filters.push(
                new Filter(
                    null,
                    'raw',
                    `'SLATypeDetails.SLAStartDate': {'$gte' : ISODate('${today_start}'), '$lte' : ISODate('${today_end}')}`
                )
            );
        }

        if (SLAExpiredDate) {
            const today_start = this.commonService.getDateToUTC(new Date(SLAExpiredDate), true);
            const today_end = this.commonService.getDateToUTC(new Date(SLAExpiredDate), false);
            filters.push(
                new Filter(
                    null,
                    'raw',
                    `'SLATypeDetails.SLAExpiredDate': {'$gte' : ISODate('${today_start}'), '$lte' : ISODate('${today_end}')}`
                )
            );
        }

        const isRegulatedFilter = this.getIsRegulatedFilter(IsRegulated);

        if (isRegulatedFilter === true) {
            filters.push(new Filter(`'RegulationInfo.IsRegulated'`, '=', true));
        } else if (isRegulatedFilter === false) {
            filters.push(new Filter(`'RegulationInfo.IsRegulated'`, '=', false));
        }

        if (BexioInvoiceId) {
            filters.push(
                new Filter(`'CaseInvoice.FileName'`, 'search', CommonService.sanitizeSearchString(BexioInvoiceId))
            );
        }

        if (IsDraft !== null) {
            filters.push(new Filter('IsDraft', '=', IsDraft));
        }
        // this condition will be satisfied only for quick-search.
        if (IsQuickSearch) {
            let searchKeyForQuery = `/${CommonService.sanitizeSearchString(SearchKey)}/i`;

            // When allCases checkbox is unchecked
            if (!AllCases) {
                // Add 'CaseStatus.Key' only for quickSearch default criteria
                filters.push(
                    new Filter(
                        null,
                        'raw',
                        `'CaseStatus.Key': {$nin: ["${SCConfig.CaseStatus.CLOSE.Key}", "${SCConfig.CaseStatus.READY_FOR_BILLING.Key}"]}`
                    )
                );
                filters.push(
                    new Filter(
                        null,
                        'raw',
                        `$and: [{$or: ${this.getQuickSearchFields(searchKeyForQuery)}},{$or: [{InspectorUserId: '${
                            LoggedUserInfo?.UserId
                        }'},{ BackofficeUserId: '${LoggedUserInfo?.UserId}'}]}]`
                    )
                );
                return filters;
            } else {
                filters.push(new Filter(null, 'raw', `$or: ${this.getQuickSearchFields(searchKeyForQuery)}`));
            }
        }

        if (ConnectedRepairCaseFilter) {
            if (ConnectedRepairCaseFilter === SCConfig.APP_CASES_FILTER_ONLY_REPAIR) {
                filters.push(new Filter(null, 'raw', `'HasConnectedRepairCase': {$eq: true}`));
            } else if (ConnectedRepairCaseFilter === SCConfig.APP_CASES_FILTER_ONLY_NON_REPAIR) {
                filters.push(new Filter(null, 'raw', `'HasConnectedRepairCase': {$ne: true}`));
            }
        }

        return filters;
    }

    private getIsRegulatedFilter(IsRegulated: any) {
        if (IsRegulated === true || IsRegulated === 'YES') {
            return true;
        } else if (IsRegulated === false || IsRegulated === 'NO') {
            return false;
        } else {
            return null;
        }
    }

    private getQuickSearchFields(value: string): string {
        return `[{InsuranceClaimNumber: ${value}}, {'PolicyHolderInfo.Name': ${value}}, {'CaseServiceList.ConstructionCompanyName': { $in: [${value}] }},{'CaseReportNumber': ${value}}]`;
    }

    getInsuranceCaseById(ItemId) {
        const filter = [new Filter('_id', '=', ItemId)];
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filter)}}`,
            PageNumber: 0,
            PageSize: 10
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                    return response;
                })
            );
    }

    private getExcelTranslationKeys() {
        return [
            {
                Keys: [
                    'INSPECTOR',
                    'BACKOFFICE',
                    'Zurich back office',
                    'Zurich inspector',
                    'Zurich SIC Anprall',
                    'AXA back office',
                    'AXA Inspector',
                    'Helvetia back office',
                    'Helvetia inspector',
                    'Generali back office',
                    'Inspector General',
                    'Generali Komplex',
                    'Allianz back office',
                    'Alliance inspector',
                    'Alliance complex',
                    'C3 Inspector',
                    'PRICE',
                    'THE_UNIT_PRICES_ARE_LOCAL.',
                    'THE_UNIT_PRICES_ARE_NOT_CUSTOMARY_FOR_THE_LOCATION.',
                    'NO_PRICE_CHECK_WAS_CARRIED_OUT',
                    'PLAUSIBILITY',
                    'SELECTION_MENU_1:',
                    'THE_WORK_AND_QUANTITIES_LISTED_SERVE_ENTIRELY_TO_REPAIR_THE_DAMAGE.',
                    'THE_WORK_AND_QUANTITIES_LISTED_SERVE_PARTLY_TO_REPAIR_THE_DAMAGE.',
                    'THE_WORK_LISTED_DOES_NOT_SERVE_TO_REPAIR_THE_DAMAGE.',
                    'WE_ASK_YOU_TO_CHECK_THE_DAMAGE-RELATED_WORK_AND_QUANTITIES_ON_SITE.',
                    'SELECTION_MENU_2:',
                    'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_COMPREHENSIBLE.',
                    'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_INCOMPREHENSIBLE.',
                    'COMPENSATION',
                    'RECOMMENDED_ACTION',
                    'IPEX_REPAIR',
                    'IPEX_REPAIR_GLASS',
                    'IPEX_REPAIR_INLINING',
                    'YES',
                    'NO',
                    'NO_DATA_FOUND',
                    'WAGE',
                    'MATERIAL',
                    'COMPLEX',
                    'EQUIPMENT',
                    'NOT_MAINTAINABLE',
                    'Meter',
                    'Stück',
                    'Liter',
                    'Diverse',
                    'Stunden'
                ]
            },
            {
                Prefix: 'APP_CASES',
                Keys: [
                    'CASE_TYPE',
                    'REMARKS',
                    'CLIENTELE',
                    'INSURANCE_PROVIDER',
                    'ASSIGNED_BACKOFFICE',
                    'ASSIGNED_INSPECTOR',
                    'PHONE',
                    'OFFER_NAME',
                    'TRADE',
                    'UNIT',
                    'QUANTITY',
                    'UNIT_PRICE',
                    'TOTAL_PRICE',
                    'COMPONENT',
                    'SAVED_COST',
                    'CONSTRUCTION_COMPANY_NAME',
                    'EMAIL',
                    'LIABILITY',
                    'SUB_ITEM_LIST',
                    'STATUS',
                    'CASE_REPORT_NUMBER',
                    'CREATE_DATE',
                    'CLOSE_DATE',
                    'REPORT_CREATION_DATE',
                    'Total_Claim_Amount',
                    'Total_Recommended_Price',
                    'Total_Difference',
                    'TESTING_CATEGORY',
                    'REGULATION',
                    'ENABLED_FOR_BILLING',
                    'TOTAL_BILLABLE_CLAIM_AMOUNT',
                    'TOTAL_BILLABLE_RECOMMENDED_PRICE'
                ]
            },
            {
                Prefix: 'CASE_LIST_REPORT',
                Keys: [
                    'SHEET_1',
                    'SHEET_2',
                    'SHEET_3',
                    'ESTIMATED_UNIT_PRICE',
                    'TOTAL_ESTIMATED_PRICE',
                    'INSPECTOR_IDENTIFIED_UNIT',
                    'INSPECTOR_IDENTIFIED_QUANTITY',
                    'INSPECTOR_IDENTIFIED_UNIT_PRICE',
                    'TOTAL_NEW_PRICE',
                    'INSPECTOR_COMMENT',
                    'BEXIO_INVOICE_ID',
                    'BEXIO_INVOICE_TIME',
                    'SERVICE_PRODUCT',
                    'INSPECTION_COMPLETED_DATE'
                ]
            },
            {
                Prefix: 'APP_CREATE_CASE',
                Keys: [
                    'CLAIM_INSURANCE_NUMBER',
                    'INSURANCE_OFFICER_NAME',
                    'INSURANCE_OFFICER_EMAIL',
                    'ORDER_DATE',
                    'ZIP_CODE_OF_USER',
                    'POLICY_HOLDER_NAME',
                    'COMPANY_NAME_OF_ISSUER',
                    'ISSUER_EMAIL_ADDRESS',
                    'SERVICE_TITLE',
                    'LIABILITY_ASSESSMENT',
                    'VAT_SET',
                    'AGE',
                    'TOTAL_SAVED_COST',
                    'LIABILITY_EVALUATION_CALCULATION',
                    'CONSTRUCTION_COMPANY_CONTRACT',
                    'CONSTRUCTION_COMPANY_UID'
                ]
            }
        ];
    }
    generateExcelReport(
        reportFileId: string,
        caseSearchFilter: CaseSearchFilter,
        generationCompleteObservable: Subject<any>,
        sortBy = 'LastUpdateDate: -1',
        languageKey: string = SCConfig.LanguageKey.German
    ) {
        const filters = this.getFiltersFromCaseSearchCriteria(caseSearchFilter);
        const translations: { Prefix?: string; Keys: string[] }[] = this.getExcelTranslationKeys();

        const payload = {
            ReportFileId: reportFileId,
            Action: 'ExportCaseReport',
            Context: 'Export',
            Language: languageKey
        };

        // this.reportService
        //     .downloadReportAfterNotification(reportFileId, undefined, payload.Context, payload.Action)
        //     .subscribe(res => generationCompleteObservable.next(res));
        return this.reportService.generateExcelReport(
            payload,
            filters,
            sortBy,
            translations,
            CommonService.getLangFromLanguageKey(languageKey)
        );
    }

    public sendBexioCollectiveInvoice(filterString: string, sortBy: string, reportFileId: string, languageKey: string) {
        let translations = this.getExcelTranslationKeys();
        const translationObservables = translations.map(translation =>
            this.reportService.getMultipleTranslationForReport(
                translation.Keys,
                translation.Prefix,
                CommonService.getLangFromLanguageKey(languageKey)
            )
        );
        return new Observable(obs => {
            combineLatest(translationObservables).subscribe(translationResponses => {
                let requestModel: SendBexioCollectiveInvoiceCommand = {
                    FilterString: filterString,
                    SortBy: sortBy,
                    ReportFileId: reportFileId,
                    ReportFileName: this.reportService.getFileNameForReport('Collective Invoice Report', reportFileId),
                    Action: 'ExportCaseReport',
                    Context: 'Export',
                    TimezoneOffsetInMinutes: new Date().getTimezoneOffset() * -1,
                    Translations: mergeAllObjects(translationResponses),
                    Language: languageKey
                };
                this.bexioService.SendBexioCollectiveInvoice(requestModel).subscribe(res => {
                    this.reportService.completeObservable(obs, res);
                });
            });
        });
    }

    getUserById(ItemId?) {
        const entityName = SCConfig.Entity.Person;
        const fields = SCConfig.UserReadableDatas.Person;
        const filters: Filter[] = [];
        const billOfEntryNumber = 'client_inspector';
        const primaryFilters = [];

        const Stringfilter = `'CreatedBy': '${ItemId}'`;

        const generatedFilter = this.graphqlService.prepareFilterFromOperator(primaryFilters);
        return this.graphqlService.getDataByGql(entityName, fields, Stringfilter);

        // .pipe(map(res  => (res && res.Data ? res.Data : [])));
    }

    searchForm() {
        return this.formBuilder.group({
            DisplayName: []
        });
    }

    caseUpdateByGql(entity, dataModel, ItemId) {
        let query: any;
        query = this.gqlQueryBuilderService.prepareUpdateModel(entity, dataModel, ItemId);
        return this.GQL.mutate({ mutation: query, errorPolicy: 'all' });
        /*.pipe(map((response: any) => {
            if (response && response.data) {
                return response;
            }
            else {
                return response;
            }
        }));*/
    }

    // setFinalInspectorData(finalInspectorDataAfterPreparing) {
    //     this.finalInspectorDetails = finalInspectorDataAfterPreparing
    // }
    // getFinalInspectorData() {
    //     return this.finalInspectorDetails;
    //  }
    // getTranslationDataForRealEstateReport(lang: string): any[] {
    //     const caseDetailsReportTranslations: { Prefix?: string; Keys: string[] }[] = [
    //         {
    //             Keys: [
    //                 'SELECTION_MENU_1:',
    //                 'SELECTION_MENU_2:',
    //                 'PRICE',
    //                 'THE_UNIT_PRICES_ARE_LOCAL.',
    //                 'THE_UNIT_PRICES_ARE_NOT_CUSTOMARY_FOR_THE_LOCATION.',
    //                 'NO_PRICE_CHECK_WAS_CARRIED_OUT',
    //                 'THE_WORK_AND_QUANTITIES_LISTED_SERVE_ENTIRELY_TO_REPAIR_THE_DAMAGE.',
    //                 'THE_WORK_AND_QUANTITIES_LISTED_SERVE_PARTLY_TO_REPAIR_THE_DAMAGE.',
    //                 'THE_WORK_LISTED_DOES_NOT_SERVE_TO_REPAIR_THE_DAMAGE.',
    //                 'WE_ASK_YOU_TO_CHECK_THE_DAMAGE-RELATED_WORK_AND_QUANTITIES_ON_SITE.',
    //                 'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_COMPREHENSIBLE.',
    //                 'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_INCOMPREHENSIBLE.',
    //                 'COMPENSATION',
    //                 'YES',
    //                 'NO',
    //                 'RECOMMENDED_ACTION',
    //                 'IPEX_REPAIR',
    //                 'IPEX_REPAIR_GLASS',
    //                 'IPEX_REPAIR_INLINING',
    //                 'LIABILITY_(ONLY_FOR_LIABILITY_DAMAGE_/_SHOULD_APPEAR_AUTOMATICALLY)',
    //                 'ACCORDING_TO_DOCUMENTS',
    //                 'ACCORDING_TO_FILE',
    //                 'AFTER_CONSULTING_COMPANY',
    //                 'AFTER_CONSULTING_CLAIMANT',
    //                 'YES',
    //                 'NO',
    //                 'Meter',
    //                 'Stück',
    //                 'Liter',
    //                 'Diverse',
    //                 'Stunden',
    //                 'MINIMUM_TIME_VALUE_INFO_MESSAGE',
    //                 ...SCConfig.ConsultationContactPersonRole
    //             ]
    //         },
    //         {
    //             Prefix: 'APP_CASES_REPORT',
    //             Keys: [
    //                 'NO.',
    //                 'DATE',
    //                 'RECOMMENDED_TOTAL_PRICE',
    //                 'AT_CURENT_VALUE',
    //                 'EXCL_VAT',
    //                 'INCL_VAT',
    //                 'DIFFERENCE',
    //                 'AUDIT_RESULT',
    //                 'OKAY',
    //                 'NOT_OKAY',
    //                 'VOTE_TAKEN',
    //                 'AUDIT_NEW_VALUE_COST',
    //                 'DIFFERENCE_CLAIM_OR_NEW_VALUE',
    //                 'FAIR_VALUE_CALCULATION_OF_AUDITED_COST',
    //                 'GENERAL_REMAKRS',
    //                 'COMMENT',
    //                 'EXAMINER',
    //                 'ANNEX',
    //                 'OFFER_OR_INVOICE',
    //                 'INTERLOCUTOR',
    //                 'COSTS',
    //                 'NET',
    //                 'VAT',
    //                 'GROSS',
    //                 'INSPECTION_DOCUMENT',
    //                 'TEST_RESULT',
    //                 'ITEM',
    //                 'QUANTITY',
    //                 'UNIT',
    //                 'TITLE',
    //                 'UNIT_PRICE',
    //                 'TOTAL',
    //                 'LIABILITY_ASSESSMENT',
    //                 'OFFER_OR_INVOICE_NO',
    //                 'FROM',
    //                 'COMPONENT',
    //                 'NEW_VALUE_OR_CURRENT_VALUE',
    //                 'NEW_VALUE',
    //                 'REMARK',
    //                 'AGE',
    //                 'LIFETIME',
    //                 'FAIR_VALUE',
    //                 'REPAIR_COST',
    //                 'DAMAGE_RELATED_COST',
    //                 'TOTAL_REPAIR_COST',
    //                 'TOTAL_CURRENT_VALUE',
    //                 'YEAR',
    //                 'YEARS',
    //                 'DAMAGE_RELATED_COST_REMARK',
    //                 'CONTACT_US'
    //             ]
    //         },
    //         {
    //             Prefix: 'APP_REAL_ESTATE_REPORT',
    //             Keys: ['PLAUSIBILITY', 'OBJECT_ADDRESS', 'CLIENT', 'AUDITED_TOTAL_PRICE', 'CLAIM']
    //         }
    //     ];
    //     const translationObservables = caseDetailsReportTranslations.map(translation => {
    //         return this.reportService.getMultipleTranslationForReport(translation.Keys, translation.Prefix, lang);
    //     });
    //     return translationObservables;
    // }

    private readonly caseDetailsReportTranslations: { Prefix?: string; Keys: string[] }[] = [
        {
            Keys: [
                'SELECTION_MENU_1:',
                'SELECTION_MENU_2:',
                'PRICE',
                'CAUSALITY',
                'THE_UNIT_PRICES_ARE_LOCAL.',
                'THE_UNIT_PRICES_ARE_NOT_CUSTOMARY_FOR_THE_LOCATION.',
                'NO_PRICE_CHECK_WAS_CARRIED_OUT',
                'PLAUSIBILITY',
                'THE_WORK_AND_QUANTITIES_LISTED_SERVE_ENTIRELY_TO_REPAIR_THE_DAMAGE.',
                'THE_WORK_AND_QUANTITIES_LISTED_SERVE_PARTLY_TO_REPAIR_THE_DAMAGE.',
                'THE_WORK_LISTED_DOES_NOT_SERVE_TO_REPAIR_THE_DAMAGE.',
                'WE_ASK_YOU_TO_CHECK_THE_DAMAGE-RELATED_WORK_AND_QUANTITIES_ON_SITE.',
                'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_COMPREHENSIBLE.',
                'THE_INFORMATION_PROVIDED_BY_THE_EXHIBITOR_IS_INCOMPREHENSIBLE.',
                'COMPENSATION',
                'YES',
                'NO',
                'RECOMMENDED_ACTION',
                'IPEX_REPAIR',
                'IPEX_REPAIR_GLASS',
                'IPEX_REPAIR_INLINING',
                'LIABILITY_(ONLY_FOR_LIABILITY_DAMAGE_/_SHOULD_APPEAR_AUTOMATICALLY)',
                'ACCORDING_TO_DOCUMENTS',
                'ACCORDING_TO_FILE',
                'AFTER_CONSULTING_COMPANY',
                'AFTER_CONSULTING_CLAIMANT',
                'YES',
                'NO',
                'Meter',
                'Stück',
                'Liter',
                'Diverse',
                'Stunden',
                'MINIMUM_TIME_VALUE_INFO_MESSAGE',
                ...SCConfig.ConsultationContactPersonRole
            ]
        },
        {
            Prefix: 'APP_CASES_REPORT',
            Keys: [
                'NO.',
                'DATE',
                'DAMAGE_NO',
                'POLICY_HOLDER',
                'RECOMMENDED_TOTAL_PRICE',
                'AT_CURENT_VALUE',
                'EXCL_VAT',
                'INCL_VAT',
                'CLAIM',
                'DIFFERENCE',
                'AUDIT_RESULT',
                'OKAY',
                'NOT_OKAY',
                'VOTE_TAKEN',
                'AUDIT_NEW_VALUE_COST',
                'DIFFERENCE_CLAIM_OR_NEW_VALUE',
                'FAIR_VALUE_CALCULATION_OF_AUDITED_COST',
                'GENERAL_REMAKRS',
                'COMMENT',
                'EXAMINER',
                'ANNEX',
                'OFFER_OR_INVOICE',
                'INTERLOCUTOR',
                'COSTS',
                'NET',
                'VAT',
                'GROSS',
                'INSPECTION_DOCUMENT',
                'TEST_RESULT',
                'ITEM',
                'QUANTITY',
                'UNIT',
                'TITLE',
                'UNIT_PRICE',
                'TOTAL',
                'LIABILITY_ASSESSMENT',
                'OFFER_OR_INVOICE_NO',
                'FROM',
                'COMPONENT',
                'NEW_VALUE_OR_CURRENT_VALUE',
                'NEW_VALUE',
                'REMARK',
                'AGE',
                'LIFETIME',
                'FAIR_VALUE',
                'REPAIR_COST',
                'DAMAGE_RELATED_COST',
                'TOTAL_REPAIR_COST',
                'TOTAL_CURRENT_VALUE',
                'YEAR',
                'YEARS',
                'DAMAGE_RELATED_COST_REMARK',
                'CONTACT_US',
                'NOT_ANSWERED_PHONE_CALL',
                'FAILED',
                'SENT',
                'PENDING',
                'TIMEOUT',
                'DELIVERED'
            ]
        },
        {
            Prefix: 'APP_CASES',
            Keys: [
                'CONSULTATION_SUCCESSFUL',
                'CONSULTATION_NOT_SUCCESSFUL',
                'PERSONS_IN_CHARGE_NOT_REACHED',
                'ADDITIONAL_DOCUMENTS_REQUESTED',
                'PERSONS_IN_CHARGE_NOT_REACHED_ASKED_FOR_CALLBACK',
                'ADDITIONAL_DOCUMENTS_REQUESTED_AT_THE_COMPANY',
                'TO'
            ]
        }
    ];

    getTranslationData(companyType: any, lang: string): any[] {
        // if (companyType == 'real_estate') return this.getTranslationDataForRealEstateReport(lang);

        const translationObservables = this.caseDetailsReportTranslations.map(translation => {
            return this.reportService.getMultipleTranslationForReport(translation.Keys, translation.Prefix, lang);
        });
        return translationObservables;
    }

    getTranslationDataForReport2(language: string) {
        let listOfAllTranslations: PCXKeyValue[] = [];
        this.caseDetailsReportTranslations.forEach(translation => {
            const translations: PCXKeyValue[] = this.reportService.getMultipleTranslationForReport2(
                translation.Keys,
                translation.Prefix,
                language
            );
            listOfAllTranslations.push(...translations);
        });
        const specificReasonTranslate: PCXKeyValue = {
            Key: PCXConstants.ConsultationReasonSubTitles.SPECIFIC_REASON,
            Value: 'Specific reason'
        };
        listOfAllTranslations.push(specificReasonTranslate);
        return listOfAllTranslations;
    }
    getInvoiceTranslationData(lang: string): any[] {
        const caseDetailsInvoiceTranslations: { Prefix?: string; Keys: string[] }[] = [
            {
                Prefix: 'APP_CASES_REPORT',
                Keys: ['DATE', 'VAT', 'ITEM', 'QUANTITY', 'TOTAL']
            },
            {
                Prefix: 'APP_CASES_INVOICE',
                Keys: [
                    'OFFER_NUMBER',
                    'CLAIM_NUMBER',
                    'INVOICE_NUMBER',
                    'BILLING_AND_AGREEMENT',
                    'OFFER_AND_INVOICE_VERFICATION',
                    'INVOICE_TOTAL_NET',
                    'INVOICE_TOTAL_GROSS',
                    'DISCLAIMER',
                    'SERVICE',
                    'UNIT_PRICE'
                ]
            }
        ];
        const translationObservables = caseDetailsInvoiceTranslations.map(translation => {
            return this.reportService.getMultipleTranslationForReport(translation.Keys, translation.Prefix, lang);
        });
        return translationObservables;
    }

    public getCaseListByBusinessService(payload: GetInsuranceCaseQueryRequestModel) {
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    sendMailByBusinessService(payload) {
        const processCaseUrl = `${this.urlResolver.getPcxCommandUrl()}SendCaseMail`;

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response['body'];
                })
            );
    }

    enableQualityCheckStatusForInspector(payload) {
        const processCaseUrl = `${this.urlResolver.getPcxCommandUrl()}EnableQualityCheckStatusForInspector`;

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response['body'];
                })
            );
    }

    createQualityCheckForm() {
        return this.formBuilder.group({
            CaseNumber: []
        });
    }
    createAssignedInspectorSelectionForm() {
        return this.formBuilder.group({
            AssignedInspectorSearchText: [],
            AssignedInspector: []
        });
    }
    createQualityCheckReminderForm() {
        return this.formBuilder.group({
            ReminderStartDate: this.formBuilder.control(null),
            ReminderNumberOfDays: this.formBuilder.control(null),
            VariableUnits: this.formBuilder.control(null)
        });
    }

    getAdditionalSearchFilterData(userInfo?) {
        if (!userInfo) {
            CasesService.hasAdditionalSearchFilterData = false;
            CasesService.additionalSearchFilterData$.next(new CaseSearchFilter());
            return;
        }
        const searchFilter: CaseSearchFilter = new CaseSearchFilter();
        this.ipexUserInformationService.getIpexUserInformation([userInfo['UserId']]).subscribe(res => {
            if (res && res.Results && res.Results.length && res.Results[0].CaseFilterString) {
                try {
                    let filterString: string = res.Results[0].CaseFilterString;
                    filterString = filterString.replaceAll("'", '"');
                    let filter: CaseSearchFilter = JSON.parse(filterString);
                    if (filter) {
                        filter = new CaseSearchFilter(filter);

                        if (filter.CaseStatus) {
                            const caseStatus = SCConfig.CaseStatusList.filter(
                                caseStatus => caseStatus.Key == filter.CaseStatus.Key
                            );
                            if (caseStatus.length) filter.CaseStatus = caseStatus[0];
                        }

                        let caseListFilter = res?.Results[0]?.CaseList;
                        if (caseListFilter?.Sort) {
                            filter.SortBy = caseListFilter.Sort;
                        }

                        filter.SearchKey = null;
                        filter.IsQuickSearch = false;

                        CasesService.hasAdditionalSearchFilterData = true;
                        CasesService.additionalSearchFilterData$.next(filter);
                        CasesService.userSearchFilterPreviousState$.next(filter);
                    } else {
                        CasesService.additionalSearchFilterData$.next(new CaseSearchFilter());
                        CasesService.hasAdditionalSearchFilterData = true;
                    }
                } catch (error) {
                    console.error(error);
                    // so that it does not crash
                    CasesService.additionalSearchFilterData$.next(new CaseSearchFilter());
                    CasesService.hasAdditionalSearchFilterData = true;
                }
            } else {
                CasesService.additionalSearchFilterData$.next(new CaseSearchFilter());
                CasesService.hasAdditionalSearchFilterData = true;
            }
        });
    }

    getAdditionalSearchFilterDataForMailBox(userInfo?): Observable<CaseSearchFilter | null> {
        const searchFilter: CaseSearchFilter = new CaseSearchFilter();
        return this.ipexUserInformationService.getIpexUserInformation([userInfo['UserId']]).pipe(
            map(res => {
                if (res && res.Results && res.Results.length && res.Results[0].CaseFilterString) {
                    let filterString: string = res.Results[0].CaseFilterString;
                    filterString = filterString.replaceAll("'", '"');
                    let filter: CaseSearchFilter = JSON.parse(filterString);
                    if (filter) {
                        filter = new CaseSearchFilter(filter);

                        if (filter.CaseStatus) {
                            const caseStatus = SCConfig.CaseStatusList.filter(
                                caseStatus => caseStatus.Key == filter.CaseStatus.Key
                            );
                            if (caseStatus.length) filter.CaseStatus = caseStatus[0];
                        }

                        return filter;
                    }
                }
                return null;
            })
        );
    }

    updateAdditionalSearchFilterData(filter?, userInfo?, sort: string = '', source: string = '') {
        if (filter.InsuranceCompany) {
            filter.InsuranceCompany = {
                ItemId: filter.InsuranceCompany.ItemId
            };
            if (filter.Department) {
                filter.Department = {
                    DepartmentId: filter.Department.DepartmentId
                };
            }
        }
        const filterString = JSON.stringify(filter).replaceAll('"', "'");

        const payload = {
            UserId: userInfo?.UserId,
            CaseFilterString: filterString,
            ...(source && {
                CaseFilterType: source,
                CaseFilter: {
                    Filter: null, // currently saving null cause we are storing the filter into another filed
                    Sort: sort
                }
            })
        };

        const caseFilterStringUrl = `${this.urlResolver.getPcxCommandUrl()}ProcessCaseFilter`;

        return this.http
            .post(caseFilterStringUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response['body'];
                })
            );
    }

    inspectorData(InspectorAnswerList) {
        let prepareData: any[] = [];
        return this.caseDefaultService.getInspectorQuestionSeedList().pipe(
            map(res => {
                if (res?.Data) {
                    const inspectorQuestionSeeds = res?.Data || [];
                    const data = this.prepareInspectorQuestionData(inspectorQuestionSeeds, InspectorAnswerList);
                    return data;
                }
                return [];
            })
        );
    }
    prepareInspectorQuestionData(inspectorQuestionSeeds: any[], inspectorAnswerList: any[]) {
        const preparedData = [];
        if (inspectorQuestionSeeds?.length > 0) {
            inspectorQuestionSeeds.forEach(data => {
                if (data.QuestionSectionList !== null) {
                    data.QuestionSectionList.forEach(element => {
                        preparedData.push({
                            QuestionId: data.ItemId,
                            QuestionTitle: data.QuestionTitle,
                            SectionId: element.SectionId,
                            SectionTitle: element.SectionTitle,
                            QuestionOptionList: element.QuestionOptionList,
                            HasRemarks: element.HasRemarks
                        });
                    });
                } else {
                    preparedData.push({
                        QuestionId: data.ItemId,
                        QuestionTitle: data.QuestionTitle,
                        SectionId: data.SectionId || null,
                        SectionTitle: data.SectionTitle || null,
                        QuestionOptionList: data.QuestionOptionList,
                        HasRemarks: data.HasRemarks
                    });
                }
            });
        }
        return this.prepareOptionMapping(preparedData, inspectorAnswerList).sort((a, b) => {
            // the array should be sorted like this
            const sortedList = [
                SCConfig.CausalityAnswer.questionId,
                SCConfig.InspectorLiabilityAnswer.questionId,
                SCConfig.InspectorCompensationAnswer.questionId
            ];

            const aIndex = sortedList.indexOf(a.QuestionId) + 1;
            const bIndex = sortedList.indexOf(b.QuestionId) + 1;

            if (aIndex && bIndex) {
                // a and b both present in our list
                return aIndex - bIndex;
            } else if (aIndex > 0) {
                // a present
                return -1;
            } else if (bIndex > 0) {
                // b present
                return 1;
            }

            // regulator recommendation should be the last one in the array
            if (a.QuestionId === SCConfig.RegulationRecommendationAnswer.questionId) {
                return 1;
            } else if (b.QuestionId === SCConfig.RegulationRecommendationAnswer.questionId) {
                return -1;
            }

            return 0;
        });
    }
    prepareOptionMapping(data, InspectorAnswerList: any) {
        let answerList: any[] = [];
        data.forEach(element => {
            (InspectorAnswerList || []).forEach(element2 => {
                if (element2.QuestionId === element.QuestionId) {
                    if (element2.SectionId === '' || element2.SectionId === null) {
                        answerList[element.QuestionId] = {
                            QuestionId: element.QuestionId,
                            QuestionTitle: element.QuestionTitle,
                            SelectedOption: element.QuestionOptionList.filter(
                                obj => obj.OptionId === element2.SelectedOptionId
                            )[0],
                            Remarks: element2.Remarks
                        };
                    } else {
                        if (answerList[element.QuestionId]) {
                            if (element2.SectionId == element.SectionId) {
                                answerList[element.QuestionId].SectionList.push({
                                    SectionTitle: element.SectionTitle,
                                    SelectedOption: element.QuestionOptionList.filter(
                                        obj => obj.OptionId === element2.SelectedOptionId
                                    )[0],
                                    Remarks: element2.Remarks
                                });
                            }
                        } else {
                            if (element2.SectionId == element.SectionId) {
                                answerList[element.QuestionId] = {
                                    QuestionId: element.QuestionId,
                                    QuestionTitle: element.QuestionTitle,
                                    SectionList: [
                                        {
                                            SectionTitle: element.SectionTitle,
                                            SelectedOption: element.QuestionOptionList.filter(
                                                obj => obj.OptionId === element2.SelectedOptionId
                                            )[0],
                                            Remarks: element2.Remarks
                                        }
                                    ]
                                };
                            }
                        }
                    }
                }
            });
        });
        let finalInspectorDataAfterPreparing = Object.values(answerList);
        return finalInspectorDataAfterPreparing;
        //  this.casesService.finalInspectorDetails$.next(this.finalInspectorDataAfterPreparing);
    }

    caseUpdateForReportAndInvoice(payload) {
        let url = `${this.urlResolver.getPcxCommandUrl()}UpdateCaseDocumentInfo`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }
    updateAssignedInspectorForCase(payload) {
        let url = `${this.urlResolver.getPcxCommandUrl()}UpdateCaseInspector`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }
    getCaseChangelog(caseId, pageNumber) {
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetCaseChangeLog`;
        let payload = {
            CaseId: caseId,
            PageSize: 10,
            PageNumber: pageNumber
        };
        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }
    sendInvoiceUsingBexio(payload: any) {
        let url = `${this.urlResolver.getExternalBexioUrl()}BexioInvoiceProcess`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    getCaseListByBusinessServiceForConnectCase(
        searchKey: string,
        InsuranceComapnayId: string,
        pageNo: number,
        itemId: string
    ) {
        let filters = [
            new Filter(null, 'raw', `'_id': {$ne: "${itemId}"}`),
            new Filter('IsDraft', '=', false),
            new Filter(null, 'raw', `'ConnectedCases.ItemId': {$nin: ["${itemId}"]}`)
        ];
        if (InsuranceComapnayId !== null) {
            filters.push(new Filter(`'CaseReferenceInfo.InsuranceComapnayId'`, '=', InsuranceComapnayId));
        }
        if (searchKey) {
            searchKey = CommonService.sanitizeSearchString(searchKey);
            let normalizedClaimNumber = searchKey.replace(/[-./\\ ]/g, '');
            filters.push(
                new Filter(
                    null,
                    'raw',
                    `$or: [
                        {NormalizedClaimNumber: ${new RegExp(normalizedClaimNumber, 'i')}}, 
                        {InsuranceClaimNumber: ${new RegExp(searchKey, 'i')}}, 
                        {CaseReportNumber: ${new RegExp(
                            searchKey,
                            'i'
                        )}}, {'PrimaryEmailConversation.Subject': ${new RegExp(searchKey, 'i')}}]`
                )
            );
        }
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: pageNo,
            PageSize: 10
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                    return response;
                })
            );
    }

    connectMultipleCasesToACase(payload: any) {
        let url = `${this.urlResolver.getPcxCommandUrl()}ConnectMultipleCases`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    disConnectCasesFromMultipleCase(payload: any) {
        let url = `${this.urlResolver.getPcxCommandUrl()}DisconnectMultipleCases`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    createEmailReceiverForm(emailReceivers: any) {
        return this.formBuilder.group({
            EmailReceivers: this.formBuilder.array((emailReceivers || []).map(this.createEmailForm.bind(this)))
        });
    }
    createEmailForm(emailReceiver?: any) {
        return this.formBuilder.group({
            EmailAddress: [(emailReceiver && emailReceiver.EmailAddress) || '', this.commonService.emailValidator],
            IsEmailSent: [false],
            IsEmailAddressSelected: [true]
        });
    }

    getInsuranceCaseByJarowaWorkOrderId(workOrderId: string) {
        // const filter = [new Filter('JarowaWorkorderInfo.WorkorderId', '=', workOrderId)];
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{ 'JarowaWorkorderInfo.WorkorderId' : '${workOrderId}' }`,
            PageNumber: 0,
            PageSize: 1
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                    return response;
                })
            );
    }
    getUploadInsuranceCaseByCaseId(caseId: string) {
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{_id: '${caseId}'}`,
            PageNumber: 0,
            PageSize: 1
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                    return response;
                })
            );
    }

    public getCollectiveInvoices(request: GetCollectiveInvoicesQueryRequestModel) {
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetCollectiveInvoices`;

        return this.http
            .post(processCaseUrl, request, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<CollectiveInvoiceModel[]>) => {
                    return response;
                })
            );
    }

    updateIsFlagOfCase(payload: any) {
        let url = `${this.urlResolver.getPcxCommandUrl()}UpdateCaseFlag`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    public sendCaseMailAndInvoice(payload: SendCaseMailAndInvoiceCommand) {
        const sendReportAndInvoiceUrl = `${this.urlResolver.getPcxCommandUrl()}SendCaseMailAndInvoice`;
        return this.http.post<IpexCommandResponse<any>>(sendReportAndInvoiceUrl, payload, {
            headers: this.header,
            withCredentials: true
        });
    }

    public updateCaseInspectorWithoutStatusChange(payload) {
        let url = `${this.urlResolver.getPcxCommandUrl()}UpdateCaseInspectorWithoutStatusChange`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    getCaseDetailsReportTranslationBlocks(): { Prefix?: string; Keys: string[] }[] {
        return this.caseDetailsReportTranslations;
    }
}
